"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import {
  QUESTIONNAIRE_ANIMATION_DURATION_MS,
  QUESTIONNAIRE_EASING_FUNCTION,
  QUESTIONNAIRE_HOLD_DURATION_MS,
  QUESTIONNAIRE_OPACITY_TARGET,
  QUESTIONNAIRE_SCALE_TARGET,
} from "../config";

type FeedbackState = "idle" | "answered" | "animating" | "completed";

interface UseAnswersProps {
  // Current state
  feedbackState: FeedbackState;

  // Should this item be visible?
  shouldShow: boolean;

  // Is the item in "answered" state (shows success colors)?
  isAnswered: boolean;

  // Inline styles for animation
  animationStyles: React.CSSProperties;

  // Call this when user provides an answer
  handleAnswer: (onComplete: () => void) => void;
}

/**
 * Shared hook for managing questionnaire item answer feedback animation
 *
 * Flow:
 * 1. idle → User answers → answered (shows success colors + checkmark)
 * 2. answered → (after holdDuration) → animating (scale down + fade)
 * 3. animating → (after animationDuration) → completed (calls parent callback)
 * 4. Parent removes item from state → component unmounts
 */
export function useAnswers(): UseAnswersProps {
  const [feedbackState, setFeedbackState] = useState<FeedbackState>("idle");
  const onCompleteRef = useRef<(() => void) | null>(null);

  const handleAnswer = useCallback((onComplete: () => void) => {
    onCompleteRef.current = onComplete;
    setFeedbackState("answered");
  }, []);

  // Manage animation timing
  useEffect(() => {
    if (feedbackState === "answered") {
      // Hold the success state, then start animation
      const holdTimer = setTimeout(() => {
        setFeedbackState("animating");
      }, QUESTIONNAIRE_HOLD_DURATION_MS);

      return () => clearTimeout(holdTimer);
    }

    if (feedbackState === "animating") {
      // Wait for animation to complete, then notify parent
      const animationTimer = setTimeout(() => {
        setFeedbackState("completed");
        onCompleteRef.current?.();
      }, QUESTIONNAIRE_ANIMATION_DURATION_MS);

      return () => clearTimeout(animationTimer);
    }
  }, [feedbackState]);

  // Compute animation styles
  const animationStyles: React.CSSProperties = {
    transition:
      feedbackState === "animating"
        ? `all ${QUESTIONNAIRE_ANIMATION_DURATION_MS}ms ${QUESTIONNAIRE_EASING_FUNCTION}`
        : "none",
    transform:
      feedbackState === "animating"
        ? `scale(${QUESTIONNAIRE_SCALE_TARGET})`
        : "scale(1)",
    opacity: feedbackState === "animating" ? QUESTIONNAIRE_OPACITY_TARGET : 1,
  };

  return {
    feedbackState,
    shouldShow: feedbackState !== "completed",
    isAnswered: feedbackState === "answered" || feedbackState === "animating",
    animationStyles,
    handleAnswer,
  };
}
